"use client";
import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaRegStar,
  FaStar,
  FaFilter,
  FaSearch,
  FaSortAmountUp,
  FaSortAmountDown,
} from "react-icons/fa";
import { LuCloudUpload } from "react-icons/lu";
import { IoAddCircleOutline, IoPeopleOutline } from "react-icons/io5";
import {
  TbBrandInstagram,
  TbBrandTelegram,
  TbBrandWhatsapp,
  TbBrandFacebook,
  TbChevronRight,
  TbChevronLeft,
  TbChevronDown,
} from "react-icons/tb";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import UploadCSVModal from "./Modals/UploadCSVModal";
import ClientModal from "./Modals/ClientModal";
import ClientDetails from "./ClientDetails/ClientDetails";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { PrimaryButton } from "../../components/buttons/primaryButton/PrimaryButton.tsx";
import { SelectButton } from "../../components/buttons/selectButton/SelectButton.tsx";
const API_BASE = REST_API_URL;

export default function Clients() {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedClientId, setSelectedClientId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showRowsDropdown, setShowRowsDropdown] = useState(false);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);

  const filterDropdownRef = useRef(null);
  const rowsDropdownRef = useRef(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const platformConfig = {
    instagram: {
      icon: <TbBrandInstagram className="w-4 h-4 text-pink-600" />,
      label: "Instagram",
      bg: "bg-pink-50 border-pink-200 text-pink-700",
    },
    whatsapp: {
      icon: <TbBrandWhatsapp className="w-4 h-4 text-emerald-600" />,
      label: "WhatsApp",
      bg: "bg-emerald-50 border-emerald-200 text-emerald-700",
    },
    telegram: {
      icon: <TbBrandTelegram className="w-4 h-4 text-blue-600" />,
      label: "Telegram",
      bg: "bg-blue-50 border-blue-200 text-blue-700",
    },
    facebook: {
      icon: <TbBrandFacebook className="w-4 h-4 text-[#020637]" />,
      label: "Facebook",
      bg: "bg-[#020637]/5 border-[#020637]/25 text-[#020637]",
    },
    in_app: {
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 500 500"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 0 C7.92 0 15.84 0 24 0 C24 51.48 24 102.96 24 156 C86.37 156 148.74 156 213 156 C213 104.52 213 53.04 213 0 C221.25 0 229.5 0 238 0 C238 116.82 238 233.64 238 354 C229.75 354 221.5 354 213 354 C213 296.25 213 238.5 213 179 C150.96 179 88.92 179 25 179 C25 236.22 25 236.22 25.09 293.44 C25.09 304.48 25.09 304.48 25.10 309.64 C25.10 313.26 25.10 316.88 25.11 320.50 C25.11 325.11 25.11 329.72 25.11 334.33 C25.11 336.03 25.12 337.73 25.12 339.43 C25.12 341.71 25.12 343.99 25.12 346.27 C25.12 347.56 25.12 348.84 25.12 350.16 C25 353 25 353 24 354 C16.08 354 8.16 354 0 354 C0 237.18 0 120.36 0 0 Z"
            fill="#0e1c7a"
            transform="translate(131,73)"
          />
          <path
            d="M0 0 C8.25 0 16.5 0 25 0 C25 116.82 25 233.64 25 354 C16.75 354 8.5 354 0 354 C0 295.92 0 237.84 0 178 C-61.71 178 -123.42 178 -187 178 C-187 177.67 -187 177.34 -187 177 C-124.96 177 -62.92 177 1 177 C2.16 180.49 2.12 183.21 2.12 186.89 C2.12 187.95 2.12 187.95 2.12 189.03 C2.12 191.39 2.12 193.76 2.11 196.13 C2.11 197.82 2.11 199.52 2.11 201.21 C2.11 205.82 2.11 210.43 2.10 215.04 C2.10 219.85 2.09 224.66 2.09 229.47 C2.09 238.59 2.08 247.71 2.07 256.82 C2.06 267.20 2.06 277.58 2.05 287.95 C2.04 309.30 2.02 330.65 2 352 C8.93 352 15.86 352 23 352 C22.67 236.83 22.34 121.66 22 3 C15.4 2.67 8.8 2.34 2 2 C2 3.91 2.01 5.82 2.01 7.79 C2.05 25.76 2.08 43.72 2.10 61.69 C2.11 70.93 2.13 80.17 2.15 89.41 C2.17 97.46 2.18 105.51 2.19 113.56 C2.19 117.82 2.20 122.08 2.21 126.35 C2.23 130.36 2.23 134.37 2.23 138.38 C2.23 139.86 2.23 141.33 2.24 142.80 C2.25 144.81 2.24 146.82 2.24 148.83 C2.24 149.96 2.24 151.08 2.25 152.24 C2 155 2 155 0 158 C-2.62 158.29 -4.99 158.39 -7.61 158.36 C-8.41 158.36 -9.20 158.36 -10.02 158.36 C-12.69 158.37 -15.36 158.35 -18.03 158.33 C-19.94 158.33 -21.84 158.32 -23.75 158.32 C-28.92 158.32 -34.09 158.29 -39.27 158.27 C-43.57 158.24 -47.87 158.23 -52.17 158.22 C-96.78 158.12 -141.39 157.61 -186 157 C-186 156.67 -186 156.34 -186 156 C-124.62 156 -63.24 156 0 156 C0 104.52 0 53.04 0 0 Z"
            fill="#0e1c7a"
            transform="translate(344,73)"
          />
        </svg>
      ),
      label: t("home.filterInApp"),
      bg: "bg-violet-50 border-violet-200 text-violet-700",
    },
  };

  useEffect(() => {
    const cId = searchParams.get("clientId");
    if (cId) {
      setSelectedClientId(Number(cId));
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!token) return;
      try {
        const headers = buildHeaders();
        const res = await axios.get(`${API_BASE}/auth/userinfo/`, { headers });
        console.log(res.data);
        setUserType(res.data?.user?.status || null);
      } catch (err) {
        console.error("❌ Failed to fetch user info:", err);
      }
    };
    fetchUserInfo();
  }, [token]);

  const openInstagramProfile = (username) => {
    if (!username || username === "null") return;
    const cleanUsername = username.replace("@", "").trim();
    window.open(`https://instagram.com/${cleanUsername}`, "_blank");
  };

  const openWhatsAppChat = (phoneNumber) => {
    if (!phoneNumber || phoneNumber.trim() === "") return;
    const cleanPhone = phoneNumber.replace(/\D/g, "");
    window.open(`https://wa.me/${cleanPhone}`, "_blank");
  };

  const openTelegramProfile = (username) => {
    if (!username || username === "null") return;
    const cleanUsername = username.replace("@", "").trim();
    window.open(`https://t.me/${cleanUsername}`, "_blank");
  };

  const openFacebookProfile = (username) => {
    if (!username || username === "null") return;
    const cleanUsername = username.replace("@", "").trim();
    window.open(`https://facebook.com/${cleanUsername}`, "_blank");
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const headers = buildHeaders();
      const res = await axios.get(`${API_BASE}/client/clients-list/`, {
        headers,
      });

      const formatted = res.data.map((item) => {
        const validInstagram =
          item.instagram_username &&
          item.instagram_username !== "null" &&
          item.instagram_username.trim() !== "";

        const validWhatsapp =
          item.whatapp_number && item.whatapp_number.trim() !== "";

        const validTelegram =
          item.telegram_username &&
          item.telegram_username.trim() !== "" &&
          item.telegram_username !== "null";

        const validFacebook =
          item.facebook_username &&
          item.facebook_username.trim() !== "" &&
          item.facebook_username !== "null";

        const platforms = [];
        if (validInstagram) platforms.push("instagram");
        if (validWhatsapp) platforms.push("whatsapp");
        if (validTelegram) platforms.push("telegram");
        if (validFacebook) platforms.push("facebook");

        return {
          id: item.id,
          name: item.name || t("home.unnamedClientFallback"),
          email: item.email || "-",
          phone: item.phone_number || "-",
          avatar:
            item.image_url ||
            "https://cdn-icons-png.flaticon.com/512/149/149071.png",
          platforms,
          income: item.total_income || 0,
          appointments: item.total_appointments || 0,
          rating: item.rating || 0,
          lastVisit: item.last_appointment_date || "-",
          joinedDate: item.created_at
            ? new Date(item.created_at).toLocaleDateString()
            : "-",
          stage: item.stage || "New",
          label: item.label || null,
          platform: item.platform || "in_app",
          instagram_username: item.instagram_username,
          whatapp_number: item.whatapp_number,
          telegram_username: item.telegram_username,
          facebook_username: item.facebook_username,
        };
      });

      setClients(formatted);
      setFilteredClients(formatted);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/sign-in";
      } else {
        console.error("❌ Failed to fetch clients:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchClients();
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        rowsDropdownRef.current &&
        !rowsDropdownRef.current.contains(event.target)
      ) {
        setShowRowsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let result = [...clients];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (client) =>
          client.name.toLowerCase().includes(query) ||
          client.email.toLowerCase().includes(query) ||
          client.phone.toLowerCase().includes(query),
      );
    }

    if (selectedPlatform !== "all") {
      result = result.filter(
        (client) =>
          client.platform === selectedPlatform ||
          (Array.isArray(client.platforms) &&
            client.platforms.includes(selectedPlatform)),
      );
    }

    result.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredClients(result);
    setPage(1);
  }, [clients, searchQuery, selectedPlatform, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <FaSortAmountUp className="w-3 h-3 text-gray-400 ml-1" />;
    }
    return sortDirection === "asc" ? (
      <FaSortAmountUp className="w-3 h-3 text-[#020637] ml-1" />
    ) : (
      <FaSortAmountDown className="w-3 h-3 text-[#020637] ml-1" />
    );
  };

  const totalPages = Math.ceil(filteredClients.length / rowsPerPage);
  const paginatedClients = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredClients.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, filteredClients]);

  const handleChangePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const rowsOptions = [
    { value: 5, label: t("home.paginationRowsCount", { value: 5 }) },
    { value: 10, label: t("home.paginationRowsCount", { value: 10 }) },
    { value: 20, label: t("home.paginationRowsCount", { value: 20 }) },
    { value: 50, label: t("home.paginationRowsCount", { value: 50 }) },
  ];

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1);
    setShowRowsDropdown(false);
  };

  if (selectedClientId) {
    return (
      <ClientDetails
        id={selectedClientId}
        onBack={() => setSelectedClientId(null)}
      />
    );
  }

  const renderPlatformIcons = (platforms, clientData) => {
    if (platforms.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-1.5">
        {platforms.map((platform, index) => {
          const config = platformConfig[platform];
          if (!config) return null;

          let onClickHandler = null;
          let isClickable = false;
          let tooltipText = config.label;

          switch (platform) {
            case "instagram":
              if (
                clientData.instagram_username &&
                clientData.instagram_username !== "null" &&
                clientData.instagram_username.trim() !== ""
              ) {
                onClickHandler = () =>
                  openInstagramProfile(clientData.instagram_username);
                isClickable = true;
                tooltipText = `Instagram: ${clientData.instagram_username}`;
              }
              break;
            case "whatsapp":
              if (
                clientData.whatapp_number &&
                clientData.whatapp_number.trim() !== ""
              ) {
                onClickHandler = () =>
                  openWhatsAppChat(clientData.whatapp_number);
                isClickable = true;
                tooltipText = `WhatsApp: ${clientData.whatapp_number}`;
              }
              break;
            case "telegram":
              if (
                clientData.telegram_username &&
                clientData.telegram_username.trim() !== "" &&
                clientData.telegram_username !== "null"
              ) {
                onClickHandler = () =>
                  openTelegramProfile(clientData.telegram_username);
                isClickable = true;
                tooltipText = `Telegram: ${clientData.telegram_username}`;
              }
              break;
            case "facebook":
              if (
                clientData.facebook_username &&
                clientData.facebook_username.trim() !== "" &&
                clientData.facebook_username !== "null"
              ) {
                onClickHandler = () =>
                  openFacebookProfile(clientData.facebook_username);
                isClickable = true;
                tooltipText = `Facebook: ${clientData.facebook_username}`;
              }
              break;
          }

          return (
            <motion.button
              key={index}
              whileHover={{ scale: isClickable ? 1.05 : 1 }}
              whileTap={{ scale: isClickable ? 0.95 : 1 }}
              onClick={onClickHandler}
              disabled={!isClickable}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${
                isClickable
                  ? "cursor-pointer hover:opacity-80 transition-opacity"
                  : "cursor-default"
              }`}
              title={tooltipText}
            >
              {config.icon}
              {config.label}
            </motion.button>
          );
        })}
      </div>
    );
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(
          <FaStar
            key={i}
            className="w-3.5 h-3.5 text-amber-500 fill-amber-500"
          />,
        );
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(
          <FaStar
            key={i}
            className="w-3.5 h-3.5 text-amber-500 fill-amber-500"
          />,
        );
      } else {
        stars.push(<FaRegStar key={i} className="w-3.5 h-3.5 text-gray-300" />);
      }
    }

    return (
      <div className="flex items-center gap-1">
        <div className="flex">{stars}</div>
        <span className="text-sm font-medium text-gray-700">
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.main
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 w-full min-h-screen"
    >
      <motion.div variants={itemVariants} className="mb-8">
        {(userType === "salon" || userType === "individual") && (
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t("home.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative" ref={filterDropdownRef}>
                <PrimaryButton
                  label={
                    selectedPlatform === "all"
                      ? t("home.filterAllPlatforms")
                      : selectedPlatform === "in_app"
                        ? t("home.filterInApp")
                        : selectedPlatform.charAt(0).toUpperCase() +
                          selectedPlatform.slice(1)
                  }
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  leftIcon={<FaFilter className="w-4 h-4 text-gray-600" />}
                  rightIcon={
                    <TbChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  }
                  variant="outline"
                />

                <AnimatePresence>
                  {showFilterDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-20"
                    >
                      {[
                        "all",
                        "instagram",
                        "whatsapp",
                        "telegram",
                        "facebook",
                        "in_app",
                      ].map((platform) => (
                        <SelectButton
                          key={platform}
                          selected={selectedPlatform === platform}
                          onClick={() => {
                            setSelectedPlatform(platform);
                            setShowFilterDropdown(false);
                          }}
                          variant="check"
                          className="first:rounded-t-xl last:rounded-b-xl"
                          label={
                            platform === "all"
                              ? t("home.filterAllPlatforms")
                              : platform === "in_app"
                                ? t("home.filterInApp")
                                : platform.charAt(0).toUpperCase() +
                                  platform.slice(1)
                          }
                        />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="flex gap-3">
                <PrimaryButton
                  label={t("home.buttonImportCSV")}
                  onClick={() => setShowUploadModal(true)}
                  leftIcon={<LuCloudUpload className="w-5 h-5" />}
                  variant="outline"
                />
                <PrimaryButton
                  label={t("home.buttonAddClient")}
                  onClick={() => setShowClientModal(true)}
                  leftIcon={<IoAddCircleOutline className="w-5 h-5" />}
                />
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <motion.div variants={itemVariants}>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    { label: t("home.tableHeaderClient"), field: "name" },
                    { label: t("home.tableHeaderLabel"), field: null },
                    { label: t("home.tableHeaderPlatforms"), field: null },
                    { label: t("home.tableHeaderRating"), field: "rating" },
                    { label: t("home.tableHeaderActions"), field: null },
                  ].map((header, index) => (
                    <th
                      key={index}
                      className={`px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider ${
                        header.field ? "cursor-pointer hover:bg-gray-100" : ""
                      }`}
                      onClick={() => header.field && handleSort(header.field)}
                    >
                      <div className="flex items-center gap-1">
                        {header.label}
                        {header.field && renderSortIcon(header.field)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex justify-center">
                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </td>
                  </tr>
                ) : paginatedClients.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center text-gray-500">
                        <IoPeopleOutline className="w-12 h-12 mb-3 text-gray-300" />
                        <p className="text-lg font-medium">
                          {t("home.stateNoClientsFound")}
                        </p>
                        <p className="text-sm mt-1">
                          {searchQuery || selectedPlatform !== "all"
                            ? t("home.stateNoClientsAdjustFilters")
                            : t("home.stateNoClientsFirstStep")}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedClients.map((client, index) => (
                    <motion.tr
                      key={client.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50 transition-colors group"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <img
                              src={client.avatar}
                              alt={client.name}
                              className="w-10 h-10 rounded-full border-2 border-white shadow"
                            />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {client.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {t("home.joinedDatePrefix", {
                                date: client.joinedDate,
                              })}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {client.label ? (
                          <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                            style={{
                              backgroundColor: client.label.color + "18",
                              borderColor: client.label.color + "55",
                              color: client.label.color,
                            }}
                          >
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: client.label.color }}
                            />
                            {client.label.name}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            {t("home.fallbackLabelEmpty")}
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {(() => {
                            const src =
                              platformConfig[client.platform] ||
                              platformConfig.in_app;
                            return (
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${src.bg}`}
                              >
                                {src.icon}
                                {src.label}
                              </span>
                            );
                          })()}
                          {renderPlatformIcons(client.platforms, client)}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {renderStars(client.rating)}
                      </td>

                      <td className="px-6 py-4">
                        <PrimaryButton
                          label={t("home.buttonViewDetails")}
                          rightIcon={
                            <TbChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                          }
                          onClick={() => setSelectedClientId(client.id)}
                          size="small"
                          className="group"
                        />
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {filteredClients.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-4 sm:mb-0">
              <span className="text-sm text-gray-700">
                {t("home.paginationRowsPerPage")}
              </span>

              <div className="relative" ref={rowsDropdownRef}>
                <PrimaryButton
                  label={t("home.paginationRowsCount", { value: rowsPerPage })}
                  onClick={() => setShowRowsDropdown(!showRowsDropdown)}
                  rightIcon={
                    <TbChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform ${showRowsDropdown ? "rotate-180" : ""}`}
                    />
                  }
                  variant="outline"
                  size="small"
                />

                <AnimatePresence>
                  {showRowsDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute bottom-full left-0 mb-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                    >
                      {rowsOptions.map((option) => (
                        <SelectButton
                          key={option.value}
                          selected={rowsPerPage === option.value}
                          onClick={() => handleRowsPerPageChange(option.value)}
                          variant="check"
                          className="px-3 py-2 text-sm first:rounded-t-lg last:rounded-b-lg"
                          label={option.label}
                        />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <span className="text-sm text-gray-500">
                {t("home.paginationRangeText", {
                  start: (page - 1) * rowsPerPage + 1,
                  end: Math.min(page * rowsPerPage, filteredClients.length),
                  total: filteredClients.length,
                })}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <PrimaryButton
                label={<TbChevronLeft className="w-5 h-5" />}
                onClick={() => handleChangePage(1)}
                disabled={page === 1}
                variant="ghost"
                size="small"
              />
              <PrimaryButton
                label={<TbChevronLeft className="w-4 h-4" />}
                onClick={() => handleChangePage(page - 1)}
                disabled={page === 1}
                variant="ghost"
                size="small"
              />
              {Array.from({ length: Math.min(5, totalPages) }).map(
                (_, index) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = index + 1;
                  } else if (page <= 3) {
                    pageNum = index + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + index;
                  } else {
                    pageNum = page - 2 + index;
                  }

                  return (
                    <PrimaryButton
                      key={pageNum}
                      onClick={() => handleChangePage(pageNum)}
                      variant={page === pageNum ? "standard" : "ghost"}
                      size="small"
                      label={pageNum}
                    />
                  );
                },
              )}

              <PrimaryButton
                label={<TbChevronRight className="w-4 h-4" />}
                onClick={() => handleChangePage(page + 1)}
                disabled={page === totalPages}
                variant="ghost"
                size="small"
              />

              <PrimaryButton
                label={<TbChevronRight className="w-5 h-5" />}
                onClick={() => handleChangePage(totalPages)}
                disabled={page === totalPages}
                variant="ghost"
                size="small"
              />
            </div>
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {showUploadModal && (
          <UploadCSVModal
            onClose={() => setShowUploadModal(false)}
            onSuccess={fetchClients}
          />
        )}

        {showClientModal && (
          <ClientModal
            onClose={() => {
              setShowClientModal(false);
              fetchClients();
            }}
          />
        )}
      </AnimatePresence>
    </motion.main>
  );
}
